/* global gdAlertify */
import React, { useState, useRef } from "react";
import axiosInstance from "../../config/axiosConfig";
import {
    buildNoEnviarWarningMessage,
    formatAuxiliarSearchLabel,
    getNoEnviarPacienteStatus,
} from "../../utils/noEnviarPaciente";
import { esAuxiliarNoActivo, ETIQUETA_NO_ACTIVA } from "../../utils/categoriaAuxiliar";

const BuscarAuxiliar = ({
    onSelect,
    conPaciente = false,
    titulo = "Buscar Auxiliar",
    pacienteId = null,
    pacienteNombre = "",
    warnOnNoEnviar = false,
    blockNoEnviar = false,
    appendNoEnviarToSelectedText = true,
}) => {
    const [auxiliares, setAuxiliares] = useState([]);
    const [textoBusqueda, setTextoBusqueda] = useState("");
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(null);

    const buscarAuxiliares = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);

        try {
            const response = await axiosInstance.get(`/auxiliar/busqueda/texto`, {
                params: {
                    search: textoBusqueda,
                    conPaciente,
                    pacienteId,
                },
                signal: abortControllerRef.current.signal,
            });
            if (!response.data.error) {
                setAuxiliares(response.data.data);
            }
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAuxiliar = (auxiliar) => {
        const noEnviarStatus = getNoEnviarPacienteStatus(auxiliar);

        // Los auxiliares con categoría NA se pueden seleccionar; solo se avisa que es NA.
        if (esAuxiliarNoActivo(auxiliar)) {
            gdAlertify.notify(
                `${formatAuxiliarSearchLabel(auxiliar, { includeNoEnviarSuffix: false })} es NA.`,
                "warning"
            );
        }

        if (blockNoEnviar && noEnviarStatus.active) {
            gdAlertify.notify(
                buildNoEnviarWarningMessage({
                    auxiliar,
                    pacienteNombre,
                }),
                "warning"
            );
            return;
        }

        if (warnOnNoEnviar && noEnviarStatus.active) {
            gdAlertify.notify(
                buildNoEnviarWarningMessage({
                    auxiliar,
                    pacienteNombre,
                }),
                "warning"
            );
        }

        onSelect(auxiliar);
        setTextoBusqueda(
            formatAuxiliarSearchLabel(auxiliar, {
                includeNoEnviarSuffix: appendNoEnviarToSelectedText,
            })
        );
        setAuxiliares([]);
    };

    const handleClearSearch = () => {
        setTextoBusqueda("");
        setAuxiliares([]);
    };

    return (
        <div className="buscador-container mb-3">
            <label className="form-label">{titulo}</label>
            <div className="input-group">
                <input
                    type="text"
                    className="form-control buscador-input"
                    value={textoBusqueda}
                    onChange={(e) => setTextoBusqueda(e.target.value)}
                    onKeyUp={buscarAuxiliares}
                    placeholder="Buscar por nombre o RUT"
                />
                {textoBusqueda && (
                    <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={handleClearSearch}
                    >
                        &times;
                    </button>
                )}
                {loading && (
                    <span className="input-group-text">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only"></span>
                        </div>
                    </span>
                )}
            </div>
            {auxiliares.length > 0 && (
                <ul className="list-group search-results">
                    {auxiliares.map((auxiliar) => {
                        const noEnviarStatus = getNoEnviarPacienteStatus(auxiliar);
                        const noActiva = esAuxiliarNoActivo(auxiliar);
                        const isBlocked = blockNoEnviar && noEnviarStatus.active;

                        return (
                            <li
                                key={auxiliar.id}
                                className={`list-group-item ${isBlocked ? 'list-group-item-secondary' : 'list-group-item-action'}`}
                                style={{
                                    cursor: isBlocked ? 'not-allowed' : 'pointer',
                                    opacity: isBlocked ? 0.65 : 1,
                                    // Categoría NA: plomo explícito para que se distinga a simple vista (solo alerta visual).
                                    backgroundColor: noActiva ? '#d6d6d6' : undefined,
                                    color: noActiva ? '#6c757d' : undefined,
                                }}
                                title={noActiva ? `Categoría NA (${ETIQUETA_NO_ACTIVA})` : undefined}
                                onClick={() => handleSelectAuxiliar(auxiliar)}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div className="small">
                                        <div className="fw-bold">
                                            {formatAuxiliarSearchLabel(auxiliar, {
                                                includeNoEnviarSuffix: false,
                                            })}
                                        </div>
                                        {auxiliar.des_categoria && (
                                            <div className="text-muted mt-1">
                                                <i className="bi bi-star me-1"></i>
                                                Categoría: {auxiliar.des_categoria}
                                            </div>
                                        )}
                                        {noEnviarStatus.active && (
                                            <div className="text-warning mt-1">
                                                <i className="bi bi-exclamation-triangle me-1"></i>
                                                {noEnviarStatus.observation || "Marcado para no enviar a este paciente."}
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                                        {noActiva && (
                                            <span className="badge bg-secondary">
                                                <i className="bi bi-slash-circle me-1"></i>
                                                NO ACTIVA
                                            </span>
                                        )}
                                        {noEnviarStatus.active && (
                                            <span className="badge bg-warning text-dark">
                                                NO ENVIAR A ESTE DOMICILIO
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default BuscarAuxiliar;
